import { useRef, useState } from 'react';

import { uploadComprovante } from '../../api/checkout';

export default function ComprovanteStep({ onContinuar }) {
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const inputRef = useRef();

  function handleArquivo(file) {
    if (!file) return;
    const allowed = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/pdf',
    ];
    if (!allowed.includes(file.type)) {
      setErro('Formato não suportado. Use JPG, PNG, WEBP ou PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErro('Arquivo muito grande. Máximo: 10 MB.');
      return;
    }
    setErro('');
    setArquivo(file);
    if (file.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    handleArquivo(e.dataTransfer.files[0]);
  }

  async function handleSubmit() {
    if (!arquivo) {
      setErro('Selecione um arquivo para continuar.');
      return;
    }
    setEnviando(true);
    setErro('');
    try {
      const resultado = await uploadComprovante(arquivo);
      onContinuar({ url: resultado.url, nota, mimetype: resultado.mimetype });
    } catch (e) {
      setErro(
        e?.response?.data?.error ?? 'Erro ao enviar arquivo. Tente novamente.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`
        @keyframes stepFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .comp-step { animation: stepFadeUp 0.35s ease both; }
        .drop-zone { transition: border-color 0.2s, background 0.2s; }
        .drop-zone:hover, .drop-zone.drag-over { border-color: #ff2d6c !important; background: #fff5f7 !important; }
      `}</style>

      <div className="comp-step">
        <h3
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: '#0f172a',
            margin: 0,
            letterSpacing: '-0.02em',
          }}
        >
          Comprovante de Vulnerabilidade
        </h3>
        <p
          style={{
            fontSize: 13,
            color: '#64748b',
            fontWeight: 500,
            marginTop: 6,
            lineHeight: 1.6,
          }}
        >
          Para garantir o Ingresso Social, precisamos de um documento que
          comprove a situação de vulnerabilidade social. Pode ser um CadÚnico,
          comprovante de bolsa família, carteira de trabalho ou similar.
        </p>
      </div>

      {/* Drop zone */}
      <div
        className="drop-zone"
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.classList.add('drag-over');
        }}
        onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
        onDrop={(e) => {
          e.currentTarget.classList.remove('drag-over');
          handleDrop(e);
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: '2px dashed #e2e8f0',
          borderRadius: 20,
          padding: '32px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: '#fafafa',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => handleArquivo(e.target.files[0])}
        />

        {preview ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <img
              src={preview}
              alt="Preview"
              style={{
                maxHeight: 180,
                maxWidth: '100%',
                borderRadius: 12,
                objectFit: 'contain',
              }}
            />
            <p style={{ fontSize: 12, color: '#10b981', fontWeight: 700 }}>
              ✓ {arquivo.name}
            </p>
            <p style={{ fontSize: 11, color: '#94a3b8' }}>Clique para trocar</p>
          </div>
        ) : arquivo ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ fontSize: 36 }}>📄</div>
            <p style={{ fontSize: 12, color: '#10b981', fontWeight: 700 }}>
              ✓ {arquivo.name}
            </p>
            <p style={{ fontSize: 11, color: '#94a3b8' }}>Clique para trocar</p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📎</div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
              Arraste o arquivo ou clique para selecionar
            </p>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              JPG, PNG, WEBP ou PDF · Máximo 10 MB
            </p>
          </>
        )}
      </div>

      {/* Nota opcional */}
      <div>
        <label
          style={{
            fontSize: 10,
            fontWeight: 900,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            display: 'block',
            marginBottom: 6,
          }}
        >
          Observação (opcional)
        </label>
        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Descreva brevemente sua situação se desejar..."
          rows={3}
          style={{
            width: '100%',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: '12px 14px',
            fontSize: 13,
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
            color: '#334155',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {erro && (
        <p
          style={{ fontSize: 12, color: '#ef4444', fontWeight: 700, margin: 0 }}
        >
          ⚠ {erro}
        </p>
      )}

      {/* Info box */}
      <div
        style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 14,
          padding: '12px 14px',
          fontSize: 12,
          color: '#166534',
          fontWeight: 600,
          lineHeight: 1.6,
        }}
      >
        🔒 Seu documento é tratado com total sigilo e usado exclusivamente para
        verificação interna. Não é compartilhado com terceiros.
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={enviando || !arquivo}
        style={{
          width: '100%',
          padding: '16px 0',
          borderRadius: 99,
          border: 'none',
          background: arquivo ? '#10b981' : '#e2e8f0',
          color: arquivo ? '#fff' : '#94a3b8',
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          cursor: arquivo ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease',
        }}
      >
        {enviando ? 'Enviando...' : 'Enviar comprovante e continuar →'}
      </button>
    </div>
  );
}
